import { DimensionRecordsColumn } from 'react-islands';
import {
  ListRequestParams,
  RequestOptions,
  listRequest,
  requestRaw,
  makeDefaultApi,
  request,
} from './request';
import { ValueMetaData } from './value_meta_data';
import { I18nText } from '../i18n_text';
import { AttributeChoiceListData } from './attribute_choice_lists';
import { UnitData } from './units';

export interface DimensionMetaData {
  id?: string;
  name_json: I18nText;
  slug?: string;
  name_slug: string;
  name_template?: string; // retrieve()-only
  is_date: boolean;
  attribute_metas: AttributeMetaData[];
  archived?: boolean;
  allow_use_as_trial_factor: boolean;
  allow_use_as_trait_additional_dimension: boolean;
  allow_use_as_trait_record_selection: boolean;
  supports_stages: boolean;
}

export const CROP_SLUG = 'crop';
export const CROP_VARIETY_SLUG = 'crop_variety';
export const FERTILIZER_SLUG = 'fertilizer';
export const CHEMICAL_SLUG = 'chemical';
export const PROTOCOL_SLUG = 'protocol';
export const PARTNER_SLUG = 'partner';
export const CULTIVATION_TYPE_SLUG = 'cultivation_type';
export const FIELD_PREPARATION_TYPE_SLUG = 'field_preparation_type';
export const IRRIGATION_TYPE_SLUG = 'irrigation_type';
export const SITE_SLUG = 'site';
export const REPETITION_SLUG = 'repetition';
export const TRIAL_TYPE_SLUG = 'stage';
export const CV_STAGE_SLUG = 'cv_stage';

export interface AttributeMetaData extends ValueMetaData {
  choice_list: AttributeChoiceListData;
  unit?: UnitData | string;
  /**
   * How the records list may filter and sort this attribute, decided by the endpoint. Read-only:
   * absent from a payload this client builds.
   */
  list_kind?: DimensionRecordsColumn['kind'];
}

export type DimensionMetaUsage =
  | 'allow_use_as_trial_factor'
  | 'allow_use_as_trait_additional_dimension'
  | 'allow_use_as_trait_record_selection'
  | 'any';
export type DimensionMetaListParams = ListRequestParams & {
  exclude_slugs?: string[];
  usage: DimensionMetaUsage;
  /** Only dimension types that can have development stages. */
  supports_stages?: boolean;
};

export const { list, retrieve, save, remove } = makeDefaultApi<DimensionMetaData, DimensionMetaListParams>(
  'dimension_metas'
);

export interface DimensionMetaCustomListParams extends ListRequestParams {
  /** Case-insensitive match on the translated name; the React table's search box travels here. */
  name_search?: string;
  /** Restricts to archived or live dimensions; null or absent answers with both. */
  archived?: boolean | null;
  /** A serialised QtFilterModel — see `main/dimension_metas/list_query.py` for the fields. */
  filter?: string;
  /** Sort keys, outermost first, `-` prefixed for descending. */
  sort?: string[];
}

export function listCustom(
  params: DimensionMetaCustomListParams,
  requestOptions?: RequestOptions
): Promise<DimensionMetaData[]> {
  return listRequest('/api/dimension_metas/custom/', params, requestOptions);
}

export function listExcludingDates(params: DimensionMetaListParams): Promise<DimensionMetaData[]> {
  return listRequest('/api/dimension_metas/?exclude_dates=true', params);
}

export function exportAll(): Promise<Blob> {
  return requestRaw('GET', '/api/dimension_metas/export/');
}

export function archive(id: string): Promise<{}> {
  return request('POST', '/api/dimension_metas/' + id + '/archive/');
}

export function unarchive(id: string): Promise<{}> {
  return request('POST', '/api/dimension_metas/' + id + '/unarchive/');
}
